import * as React from 'react';
import {
  Box, AppBar, Toolbar, Button, Container, Typography,
  MenuItem, Drawer, Stack, Divider, Popper,
  Paper, ClickAwayListener, MenuList, Grow, Grid
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DownloadIcon from '@mui/icons-material/Download';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LaptopIcon from '@mui/icons-material/Laptop';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getAllBlogs } from '../pages/blogs/index'; // Update the path as necessary

// Theme configuration — single brand color (#FFCA18) + neutrals
const THEME = {
  colors: {
    primary: '#FFCA18',
    primaryHover: '#E6AF00',
    primaryTint: 'rgba(255, 202, 24, 0.10)',
    secondary: '#1a1a1a',
    text: {
      primary: '#1a1a1a',
      secondary: '#666666'
    },
    border: {
      yellow: 'rgba(255, 202, 24, 0.5)'
    },
    shadows: {
      yellow: '0 4px 14px rgba(255, 202, 24, 0.25)'
    }
  },
  buttons: {
    height: '42px', // Increased from 36px
    borderRadius: '10px', // Slightly more rounded
    fontSize: {
      small: '0.75rem',
      normal: '0.9rem'
    },
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
  }
};

// Custom Link component that scrolls to top after navigation
const ScrollToTopLink = React.forwardRef(({ to, onClick, children, ...props }, ref) => {
  const handleClick = (event) => {
    if (onClick) onClick(event);
    
    // After the link is clicked and navigation happens, scroll to top
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <Link to={to} onClick={handleClick} ref={ref} {...props}>
      {children}
    </Link>
  );
});

// Navigation Button Component
const NavButton = ({ children, onClick, variant = "text", href }) => (
  <Button
    variant={variant}
    onClick={onClick}
    href={href}
    sx={{
      height: THEME.buttons.height,
      px: 2,
      borderRadius: THEME.buttons.borderRadius,
      color: THEME.colors.text.primary,
      fontWeight: 700,  // Made bolder
      fontSize: THEME.buttons.fontSize.normal,
      transition: THEME.buttons.transition,
      '&:hover': {
        backgroundColor: 'rgba(0,0,0,0.04)',
        transform: 'translateY(-1px)',
      }
    }}
  >
    {children}
  </Button>
);

// Navigation Button with Dropdown Component
const NavButtonWithDropdown = ({ children, menuItems }) => {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  // Group blogs by month/year for better organization
  const groupedBlogs = React.useMemo(() => {
    // Create categories (you can customize these)
    const categories = [
      { name: "Recent Posts", items: [] },
      { name: "Popular Guides", items: [] },
      { name: "Walmart Strategies", items: [] },
    ];

    // Distribute blogs into categories
    // For simplicity, we'll put most recent in "Recent Posts" 
    // and divide the rest between the other categories
    if (menuItems.length > 0) {
      // First 5 to Recent Posts
      categories[0].items = menuItems.slice(0, 5);

      // Next blogs to Popular Guides
      const remainingCount = menuItems.length - 5;
      const halfRemaining = Math.floor(remainingCount / 2);

      categories[1].items = menuItems.slice(5, 5 + halfRemaining);
      categories[2].items = menuItems.slice(5 + halfRemaining);
    }

    return categories;
  }, [menuItems]);

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={handleToggle}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          height: THEME.buttons.height,
          px: 2,
          borderRadius: THEME.buttons.borderRadius,
          color: THEME.colors.text.primary,
          fontWeight: 700,
          fontSize: THEME.buttons.fontSize.normal,
          transition: THEME.buttons.transition,
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
            transform: 'translateY(-1px)',
          }
        }}
      >
        {children}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        style={{ zIndex: 1500 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                mt: 1,
                maxHeight: '80vh',
                overflowY: 'auto',
                width: '900px', // Wider for multi-column
                borderRadius: '12px'
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  <Grid container sx={{ p: 2 }}>
                    {groupedBlogs.map((category, catIndex) => (
                      <Grid item xs={6} key={catIndex} sx={{ p: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: THEME.colors.text.primary,
                            borderBottom: '2px solid',
                            borderColor: THEME.colors.primary,
                            pb: 1,
                            mb: 2
                          }}
                        >
                          {category.name}
                        </Typography>

                        <MenuList sx={{ p: 0 }}>
                          {category.items.map((item, index) => (
                            <MenuItem
                              key={index}
                              component={ScrollToTopLink}
                              to={item.url}
                              onClick={handleClose}
                              sx={{
                                py: 1,
                                px: 1.5,
                                borderRadius: '8px',
                                mb: 0.5,
                                '&:hover': {
                                  backgroundColor: 'rgba(255,204,28,0.08)'
                                }
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  color: THEME.colors.text.primary,
                                  textAlign: 'left',
                                  lineHeight: 1.4
                                }}
                              >
                                {item.title}
                              </Typography>
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Grid>
                    ))}
                  </Grid>

                  <Box sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0,0,0,0.08)', backgroundColor: 'rgba(0,0,0,0.02)' }}>
                    <Button
                      component={ScrollToTopLink}
                      to="/blogs"
                      onClick={handleClose}
                      variant="contained"
                      sx={{
                        fontWeight: 700,
                        backgroundColor: THEME.colors.primary,
                        color: THEME.colors.text.primary,
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: THEME.colors.primaryHover
                        }
                      }}
                    >
                      View All Articles
                    </Button>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

// Apps dropdown — Chrome extension / mobile / desktop in one neutral menu
const AppsMenu = ({ extUrl, setMobileAppPopup }) => {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);

  const items = [
    {
      icon: <DownloadIcon sx={{ fontSize: 18, mr: 1.5 }} />,
      label: 'Chrome Extension',
      onClick: () => { window.location.href = extUrl; },
    },
    {
      icon: <PhoneIphoneIcon sx={{ fontSize: 18, mr: 1.5 }} />,
      label: 'Mobile App',
      // Pages like /pricing render the bar without the popup handler;
      // fall back to the home page which opens it via the query param.
      onClick: () => {
        if (setMobileAppPopup) setMobileAppPopup(true);
        else window.location.href = '/?downloadMobileApp=1';
      },
    },
    {
      icon: <LaptopIcon sx={{ fontSize: 18, mr: 1.5 }} />,
      label: 'Desktop App',
      onClick: () => { window.location.href = 'https://marter.app'; },
    },
  ];

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={() => setOpen((prev) => !prev)}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          height: THEME.buttons.height,
          px: 2,
          borderRadius: THEME.buttons.borderRadius,
          color: THEME.colors.text.primary,
          fontWeight: 700,
          fontSize: THEME.buttons.fontSize.normal,
          transition: THEME.buttons.transition,
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
            transform: 'translateY(-1px)',
          }
        }}
      >
        Apps
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        disablePortal
        style={{ zIndex: 1500 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'left top' }}>
            <Paper elevation={3} sx={{ mt: 1, borderRadius: '12px', minWidth: '210px' }}>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MenuList sx={{ p: 1 }}>
                  {items.map((item) => (
                    <MenuItem
                      key={item.label}
                      onClick={() => { setOpen(false); item.onClick(); }}
                      sx={{
                        borderRadius: '8px',
                        py: 1.2,
                        fontWeight: 500,
                        '&:hover': { backgroundColor: THEME.colors.primaryTint }
                      }}
                    >
                      {item.icon}
                      {item.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

// Trial Button Component — the only colored button in the navbar
const TrialButton = ({ onClick }) => (
  <Button
    variant="contained"
    onClick={onClick}
    sx={{
      backgroundColor: THEME.colors.primary,
      color: THEME.colors.text.primary,
      height: THEME.buttons.height,
      px: 3,
      fontSize: THEME.buttons.fontSize.normal,
      fontWeight: 700,
      borderRadius: THEME.buttons.borderRadius,
      transition: THEME.buttons.transition,
      textTransform: 'none',
      minWidth: '140px',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: THEME.colors.primaryHover,
        transform: 'translateY(-2px)',
        boxShadow: THEME.colors.shadows.yellow,
      }
    }}
  >
    Start Free Trial
  </Button>
);

// Main AppBar Component
function AppAppBar({ setEmailModal, setMobileAppPopup }) {
  const [open, setOpen] = React.useState(false);
  const extUrl = "https://chromewebstore.google.com/detail/marter/ldadolhckmdcpiodmjjgilbbmiakbhgp";
  const navigate = useNavigate();
  const location = useLocation();

  // Load all blogs for the dropdown menu and SEO-friendly links
  const blogs = React.useMemo(() => {
    try {
      return getAllBlogs(); // Get ALL blogs, not just first 10
    } catch (error) {
      console.warn("Error loading blogs for navbar:", error);
      return [];
    }
  }, []);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const scrollToSection = (sectionId) => {
    const isHomePage = location.pathname === '/';
    
    if (!isHomePage) {
      // If not on home page, navigate to home page first
      navigate('/', { replace: true });
      
      // Then scroll to the section after a brief delay to ensure the page has loaded
      setTimeout(() => {
        const sectionElement = document.getElementById(sectionId);
        if (sectionElement) {
          const offset = 128;
          const elementPosition = sectionElement.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - offset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }, 100); // 100ms delay
    } else {
      // Already on home page, just scroll
      const sectionElement = document.getElementById(sectionId);
      if (sectionElement) {
        const offset = 128;
        const elementPosition = sectionElement.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }
    
    // Close mobile menu if open
    setOpen(false);
  };

  const handleLogoClick = () => {
    // Navigate to home page
    navigate('/');
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ py: 0.75 }}>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
            <img
              src="/fulllogot.png"
              style={{ height: 32, cursor: 'pointer' }}
              alt="Marter"
              onClick={handleLogoClick}
            />

            {/* Desktop Navigation */}
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              <NavButton onClick={() => scrollToSection('pricing')}>
                Pricing
              </NavButton>

              {/* Blog dropdown with all blogs for SEO */}
              <NavButtonWithDropdown menuItems={blogs}>
                Blog
              </NavButtonWithDropdown>

              <NavButton onClick={() => scrollToSection('faq')}>
                FAQ
              </NavButton>

              <AppsMenu extUrl={extUrl} setMobileAppPopup={setMobileAppPopup} />

              <Box sx={{ mx: 1 }}>
                <Divider orientation="vertical" flexItem />
              </Box>

              <NavButton onClick={() => { window.location.href = "/pioneers-dashboard"; }}>
                Pioneers
              </NavButton>
            </Stack>
          </Box>

          {/* CTA Button */}
          <Box sx={{ display: { xs: 'none', md: 'block' }, ml: 2 }}>
            <TrialButton onClick={() => setEmailModal(true)} />
          </Box>

          {/* Mobile Menu */}
          <Box sx={{ display: { md: 'none' } }}>
            <Button
              variant="text"
              aria-label="menu"
              onClick={toggleDrawer(true)}
              sx={{
                minWidth: '40px',
                height: '40px',
                p: '8px',
                borderRadius: '10px',
                color: THEME.colors.secondary,
                '&:hover': {
                  backgroundColor: 'rgba(255,204,28,0.08)',
                }
              }}
            >
              <MenuIcon />
            </Button>
            <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
              <Box
                sx={{
                  width: '80vw',
                  p: 2,
                  backgroundColor: 'background.paper',
                  flexGrow: 1,
                }}
              >
                <MenuItem onClick={() => scrollToSection('pricing')}>
                  Pricing
                </MenuItem>

                <Typography variant="subtitle1" sx={{ fontWeight: 600, my: 1.5, ml: 2 }}>
                  Blog Articles
                </Typography>

                {/* Mobile blog list - accordion style for better organization */}
                <Box sx={{ maxHeight: '50vh', overflowY: 'auto', mb: 2 }}>
                  {blogs.slice(0, 15).map((blog, index) => (
                    <MenuItem
                      key={index}
                      component={ScrollToTopLink}
                      to={blog.url}
                      onClick={toggleDrawer(false)}
                      sx={{
                        py: 1,
                        pl: 3,
                        fontSize: '0.9rem',
                        textAlign: 'left',
                        borderRadius: '6px',
                        my: 0.5
                      }}
                    >
                      {blog.title}
                    </MenuItem>
                  ))}
                  <MenuItem
                    component={ScrollToTopLink}
                    to="/blogs"
                    onClick={toggleDrawer(false)}
                    sx={{
                      fontWeight: 600,
                      color: THEME.colors.primary,
                      textAlign: 'left',
                      mt: 1
                    }}
                  >
                    View All Blogs →
                  </MenuItem>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <MenuItem onClick={() => scrollToSection('faq')}>FAQ</MenuItem>

                <Typography variant="subtitle1" sx={{ fontWeight: 600, my: 1.5, ml: 2 }}>
                  Apps
                </Typography>
                <MenuItem
                  onClick={() => window.location.href = extUrl}
                  sx={{ pl: 3, textAlign: 'left' }}
                >
                  <DownloadIcon sx={{ fontSize: 18, mr: 1.5 }} />
                  Chrome Extension
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (setMobileAppPopup) setMobileAppPopup(true);
                    else window.location.href = '/?downloadMobileApp=1';
                  }}
                  sx={{ pl: 3, textAlign: 'left' }}
                >
                  <PhoneIphoneIcon sx={{ fontSize: 18, mr: 1.5 }} />
                  Mobile App
                </MenuItem>
                <MenuItem
                  onClick={() => window.location.href = "https://marter.app"}
                  sx={{ pl: 3, textAlign: 'left' }}
                >
                  <LaptopIcon sx={{ fontSize: 18, mr: 1.5 }} />
                  Desktop App
                </MenuItem>
                <MenuItem
                  onClick={() => window.location.href = "/pioneers-dashboard"}
                  sx={{ textAlign: 'left' }}
                >
                  Pioneers Dashboard
                </MenuItem>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <TrialButton onClick={() => setEmailModal(true)} />
                  <Button color="error" onClick={toggleDrawer(false)}>Close</Button>
                </Box>
              </Box>
            </Drawer>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default AppAppBar;